import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../../components/FeatherIcon'
import { LoadingBlock } from '../../../components/Spinner'
import { Sentinel } from '../../../library/Sentinel'
import { useMediaSrc } from '../../../library/useMediaSrc'
import { Avatar } from '../../Avatar'
import { Dialog } from '../../home/Dialog'
import type { OfApi } from '../../ofApi'
import { categoriesQuery, profileUrl, REFERER, toCategory, type BookmarkCategory, type DiscoverUser } from './discoverData'

/** "N collaborators": every creator of a tile as a profile card (DISCOVER_MENTIONED_USERS). */
export function CollaboratorsModal({ ids, users, onClose }: { ids: number[]; users: Record<number, DiscoverUser>; onClose: () => void }) {
  const { t } = useTranslation('ofMore', { keyPrefix: 'discoverPage' })
  return (
    <Dialog
      title={t('collaborators', { count: ids.length })}
      onClose={onClose}
      actions={
        <button type="button" className="lib-btn" onClick={onClose}>
          {t('close')}
        </button>
      }
    >
      <div className="ofdc-cards">
        {ids.map((id) => (
          <ProfileCard key={id} id={id} user={users[id]} />
        ))}
      </div>
    </Dialog>
  )
}

function ProfileCard({ id, user }: { id: number; user: DiscoverUser | undefined }) {
  const { t } = useTranslation('ofMore', { keyPrefix: 'discoverPage' })
  const { ref, src } = useMediaSrc(user?.cover ?? '')
  return (
    <a className="ofdc-pcard" href={profileUrl(user, id)} target="_blank" rel="noopener noreferrer">
      <span ref={ref as React.RefObject<HTMLSpanElement>} className="ofdc-pcard-cover">
        {src && <img src={src} alt="" onError={(e) => (e.currentTarget.style.visibility = 'hidden')} />}
      </span>
      {user?.isFree && <span className="ofdc-pcard-free">{t('free')}</span>}
      <span className="ofdc-pcard-body">
        <span className="ofdc-pcard-avatar">
          <Avatar user={user} size={64} />
        </span>
        <span className="ofdc-pcard-name">
          {user?.name ?? ''}
          {user?.verified && <FeatherIcon name="check-circle" size={14} className="ofp-verified" />}
        </span>
        <span className="ofdc-pcard-handle">@{user?.username || id}</span>
      </span>
    </a>
  )
}

type CategoriesPayload = { list?: Record<string, unknown>[]; hasMore?: boolean }

/** OnlyFans refuses a category named like the built-in one ("All bookmarks", in OnlyFans' English). */
const ALL_BOOKMARKS = 'All bookmarks'
const NAME_MAX = 64

/**
 * "Add to bookmarks" (BOOKMARK modal, from the tile's "Add to collection"):
 * the bookmark categories, ticked where this post is; "New category" creates one.
 */
export function BookmarkModal({
  of,
  postId,
  canWrite,
  onBookmarked,
  onClose,
  say,
}: {
  of: OfApi
  postId: number
  canWrite: boolean
  onBookmarked: () => void
  onClose: () => void
  say: (text: string) => void
}) {
  const { t } = useTranslation('ofMore', { keyPrefix: 'discoverPage' })
  const [list, setList] = useState<BookmarkCategory[]>([])
  const [hasMore, setHasMore] = useState(true)
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)
  const [busyIds, setBusyIds] = useState<number[]>([])
  const [creating, setCreating] = useState(false)
  const offset = useRef(0)
  const busy = useRef(false)

  const loadMore = useCallback(async () => {
    if (busy.current || !hasMore) return
    busy.current = true
    setLoading(true)
    setFailed(false)
    try {
      const p = await of.get<CategoriesPayload>('posts/bookmarks/categories', categoriesQuery(postId, offset.current), REFERER)
      const page = (p?.list ?? []).map(toCategory)
      offset.current += 10
      setList((all) => [...all, ...page.filter((c) => !all.some((x) => x.id === c.id))])
      setHasMore(p?.hasMore === true)
    } catch {
      setFailed(true)
      setHasMore(false)
    } finally {
      busy.current = false
      setLoading(false)
    }
  }, [of, postId, hasMore])

  useEffect(() => {
    void loadMore()
    // First page once; the sentinel asks the rest.
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async (c: BookmarkCategory) => {
    if (!canWrite || busyIds.includes(c.id)) return
    const adding = !c.hasPost
    const set = (patch: Partial<BookmarkCategory>) => setList((all) => all.map((x) => (x.id === c.id ? { ...x, ...patch } : x)))
    setBusyIds((b) => [...b, c.id])
    set({ hasPost: adding })
    try {
      const r = await of.send<Record<string, unknown>>(adding ? 'POST' : 'DELETE', `posts/bookmarks/categories/${c.id}/${postId}`, REFERER)
      if (adding && r?.success !== true) throw new Error(t('error'))
      const back = (adding ? r?.bookmarkCategory : r) as Record<string, unknown> | undefined
      // The answer is the category with its new count.
      set({ hasPost: adding, ...(typeof back?.postCount === 'number' ? { postCount: back.postCount } : {}) })
      if (adding) onBookmarked()
    } catch (e) {
      set({ hasPost: !adding })
      say(e instanceof Error && e.message ? e.message : t('error'))
    } finally {
      setBusyIds((b) => b.filter((id) => id !== c.id))
    }
  }

  if (creating) {
    return (
      <NewCategory
        onCancel={() => setCreating(false)}
        onSave={async (name) => {
          if (name.trim().toLowerCase() === ALL_BOOKMARKS.toLowerCase()) {
            say(t('nameExists'))
            return
          }
          try {
            const r = await of.send<Record<string, unknown>>('POST', 'posts/bookmarks/categories', REFERER, { name })
            if (r && r.id !== undefined) setList((all) => [...all.filter((x) => x.id !== Number(r.id)), toCategory(r)])
            setCreating(false)
          } catch (e) {
            say(e instanceof Error && e.message ? e.message : t('error'))
          }
        }}
      />
    )
  }

  return (
    <Dialog
      title={t('addToBookmarks')}
      onClose={onClose}
      actions={
        <>
          <button type="button" className="lib-btn ofdc-new-cat" disabled={!canWrite} onClick={() => setCreating(true)}>
            <FeatherIcon name="plus" size={14} />
            {t('newCategory')}
          </button>
          <button type="button" className="lib-btn" onClick={onClose}>
            {t('close')}
          </button>
        </>
      }
    >
      <div className="ofdc-cats" role="group" aria-label={t('addToBookmarks')}>
        {list.map((c) => (
          <button
            key={c.id}
            type="button"
            role="checkbox"
            aria-checked={c.hasPost}
            className="lib-menu-item ofdc-cat"
            disabled={!canWrite || busyIds.includes(c.id)}
            onClick={() => void toggle(c)}
          >
            <span className="lib-menu-label">{c.name}</span>
            <span className={`ofdc-box ${c.hasPost ? 'is-on' : ''}`} aria-hidden="true">
              {c.hasPost && <FeatherIcon name="check" size={12} />}
            </span>
          </button>
        ))}
        {loading && <LoadingBlock pad={list.length ? '10px 0' : '28px 0'} label={t('loading')} />}
        {!loading && failed && (
          <p className="ofdc-empty">
            {t('error')}{' '}
            <button
              type="button"
              className="ofp-link-btn"
              onClick={() => {
                setHasMore(true)
                setFailed(false)
              }}
            >
              {t('retry')}
            </button>
          </p>
        )}
        {!loading && !failed && !hasMore && list.length === 0 && (
          <p className="ofdc-empty">
            <FeatherIcon name="bookmark" size={28} />
            <span>{t('noBookmarksYet')}</span>
          </p>
        )}
        {hasMore && !loading && <Sentinel onVisible={() => void loadMore()} token={list.length} />}
      </div>
    </Dialog>
  )
}

/** "Create new bookmark": a name of up to 64 characters. */
function NewCategory({ onCancel, onSave }: { onCancel: () => void; onSave: (name: string) => Promise<void> }) {
  const { t } = useTranslation('ofMore', { keyPrefix: 'discoverPage' })
  const [name, setName] = useState('')
  const [saving, setSaving] = useState(false)
  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim() || saving) return
    setSaving(true)
    try {
      await onSave(name)
    } finally {
      setSaving(false)
    }
  }
  return (
    <Dialog title={t('createNewBookmark')} onClose={onCancel}>
      <form className="ofdc-newcat" onSubmit={(e) => void submit(e)}>
        <label className="ofdc-field">
          <span className="ofdc-field-label">{t('bookmarkName')}</span>
          <input className="ofp-input" value={name} maxLength={NAME_MAX} required autoFocus onChange={(e) => setName(e.target.value)} />
          <span className="ofdc-counter">
            {name.length} / {NAME_MAX}
          </span>
        </label>
        <div className="ofp-modal-actions">
          <button type="button" className="lib-btn" onClick={onCancel}>
            {t('cancel')}
          </button>
          <button type="submit" className="lib-btn lib-btn--primary" disabled={!name.trim() || saving}>
            {t('save')}
          </button>
        </div>
      </form>
    </Dialog>
  )
}
